// The table's state as one hook: the reducer, synchronous publication and controlled input. A committed
// interaction applies locally and publishes its complete replacement state, with its reason, inside the
// same event turn, so it is published before any request the change causes starts (requests start after
// the render). State the consumer supplies replaces local state and publishes nothing, and so does a
// replaced configuration reconciling the state to its columns.

import { useCallback, useRef, useState } from 'react'

import { ACTIONS } from '../types'
import { pruneFilters } from '../query/filters'
import { reconcileSort } from '../query/sort'
import { reconcileOrder } from '../view/view'
import { fromControlled } from './controlled'
import { eventOf, publishedState } from './publish'
import { initialState, reducer } from './reducer'

// What a replaced configuration changes in the state: the reconciling defaults, the column order and hidden
// set over the configured columns, and a query without filters or a user sort on columns that are gone.
// Null when nothing changes.
const fromConfig = (state, config) => {
  const columns = config.columns || []
  const keys = columns.map(column => column.key)
  const { defaults } = initialState(config)
  const sameColumns =
    state.view.order.length === keys.length &&
    keys.every(key => state.view.order.includes(key))
  const hidden = state.view.hidden.filter(key => keys.includes(key))
  const filters = pruneFilters(state.query.filters, { keys })
  const sort = reconcileSort(state.query.sort, config.defaultSort, { keys })
  const partial = {}
  if (JSON.stringify(defaults) !== JSON.stringify(state.defaults))
    partial.defaults = defaults
  if (!sameColumns || hidden.length !== state.view.hidden.length)
    partial.view = { order: reconcileOrder(state.view.order, columns), hidden }
  if (
    sort !== state.query.sort ||
    Object.keys(filters).length !== Object.keys(state.query.filters).length
  )
    partial.query = { filters, sort }
  return Object.keys(partial).length ? partial : null
}

/**
 * @param {object} options
 * @param {import('../types').NormalizedConfig} options.config
 * @param {?Function} [options.onStateChange] (state, {reason, change})
 * @param {?object} [options.controlledState] published-shape state from the consumer
 * @param {?number|(() => ?number)} [options.eligibleTotal] the exact eligible matching count, for
 *   query-scoped selection; a getter reads the count the source derived after this hook ran
 * @returns {[import('../types').TableState, (action: object) => void, () => import('../types').TableState]}
 *   the state, apply, and a reader of the latest committed state, which is ahead of the render between a
 *   commit and the render it causes
 */
export const useTableState = ({
  config,
  onStateChange = null,
  controlledState = null,
  eligibleTotal = null,
}) => {
  const [state, setState] = useState(() => initialState(config))
  const current = useRef(state)
  const latest = useRef({ config, onStateChange, eligibleTotal })
  latest.current = { config, onStateChange, eligibleTotal }
  const published = useRef(null)
  if (published.current === null)
    published.current = JSON.stringify(publishedState(state, config))

  // counts controlled and configuration changes that replaced the request, so a request a person started
  // and a consumer then replaced no longer owns the focus return
  const superseded = useRef(0)
  const apply = useCallback(action => {
    const prev = current.current
    const next = reducer(prev, action)
    if (next === prev) return
    current.current = next
    setState(next)
    if (
      action.reason === 'controlled' &&
      (next.query !== prev.query ||
        next.page !== prev.page ||
        next.pageSize !== prev.pageSize)
    )
      superseded.current += 1
    const {
      config: cfg,
      onStateChange: notify,
      eligibleTotal: count,
    } = latest.current
    const shape = publishedState(next, cfg, {
      eligibleTotal: typeof count === 'function' ? count() : count,
    })
    const serialized = JSON.stringify(shape)
    if (serialized === published.current) return
    published.current = serialized
    if (action.reason === 'controlled' || !notify) return
    notify(shape, eventOf(action.reason))
  }, [])

  // a replaced configuration reconciles the state during render, before any request reads it
  const seenConfig = useRef(config)
  if (config !== seenConfig.current) {
    seenConfig.current = config
    const partial = fromConfig(current.current, config)
    if (partial)
      apply({
        type: ACTIONS.controlledMerge,
        state: partial,
        reason: 'controlled',
      })
  }

  // a changed controlled input is merged during render, so no frame shows the replaced state
  const controlledKey = controlledState ? JSON.stringify(controlledState) : null
  const seen = useRef(null)
  if (controlledKey !== seen.current) {
    seen.current = controlledKey
    if (controlledState) {
      const partial = fromControlled(current.current, controlledState, config)
      if (Object.keys(partial).length)
        apply({
          type: ACTIONS.controlledMerge,
          state: partial,
          reason: 'controlled',
        })
    }
  }

  const read = useCallback(() => current.current, [])

  return [current.current, apply, read, superseded.current]
}
